import { ReactNode, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Hourglass, Sparkles } from 'lucide-react';
import { usePaymentStore, QuotaKind } from '../state/usePaymentStore';
import { usePaymentGate } from '../hooks/usePaymentGate';
import { WebPurchaseNotice } from './WebPurchaseNotice';

/**
 * ─── QuotaGate ───────────────────────────────────────────────────────────────
 *
 * Verrou posé sur une route premium (pratique / simulation), piloté par les
 * limites EFFECTIVES de l'utilisateur (entitlements de son plan s'il est
 * abonné, quota gratuit sinon — voir usePaymentStore.effectiveLimits) :
 *
 *   payments OFF (admin)   → passthrough total, la fonctionnalité est libre.
 *   limite illimitée (null)→ passthrough (consumeQuota ne décompte pas).
 *   sinon                  → consomme 1 essai du jour à l'entrée ;
 *                            quota épuisé → paywall (plans + retour).
 *
 * La consommation est idempotente pour une même session (clé = pathname,
 * fenêtre de grâce 30 min) : un refresh ou le double-mount StrictMode ne
 * décomptent pas deux fois.
 */
interface QuotaGateProps {
  kind: QuotaKind;
  children: ReactNode;
}

export function QuotaGate({ kind, children }: QuotaGateProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { paymentsEnabled, canPurchaseInApp, hasActiveSubscription, isExpired, quota } = usePaymentGate();
  const currentPlan = usePaymentStore((s) =>
    s.subscription ? s.plans.find((p) => p.id === s.subscription!.planId) : undefined,
  );
  // Prix d'entrée (plan actif le moins cher) : affiché dans le paywall pour que
  // l'utilisateur connaisse le coût AVANT de cliquer — un paywall sans prix
  // impose un clic de plus pour l'information qui décide de tout.
  // On sélectionne le tableau `plans` (référence stable) puis on dérive le prix
  // via useMemo : renvoyer un objet neuf DEPUIS le sélecteur zustand créerait
  // une nouvelle référence à chaque rendu → boucle de rendu infinie (React #185).
  const plans = usePaymentStore((s) => s.plans);
  const entryPrice = useMemo(() => {
    const active = plans.filter((p) => p.isActive);
    if (active.length === 0) return null;
    const cheapest = active.reduce((a, b) => (b.price < a.price ? b : a));
    return { price: cheapest.price, currency: cheapest.currency };
  }, [plans]);

  // Lazy initializer : décision prise UNE fois à l'entrée de la route (et
  // rejouable sans double-décompte grâce à la fenêtre de grâce du store).
  // consumeQuota rend true directement quand la limite effective est illimitée.
  const [allowed] = useState<boolean>(() => {
    if (!paymentsEnabled) return true;
    return usePaymentStore.getState().consumeQuota(kind, location.pathname);
  });

  if (!paymentsEnabled || allowed) return <>{children}</>;

  const limit = kind === 'practice' ? quota?.practiceLimit ?? 0 : quota?.simulationLimit ?? 0;
  const noun =
    limit > 1
      ? kind === 'practice'
        ? `${limit} sessions de pratique`
        : `${limit} simulations`
      : kind === 'practice'
        ? '1 session de pratique'
        : '1 simulation';

  // Trois situations : abonné dont le plan a une limite quotidienne, abonné
  // expiré, ou utilisateur gratuit. Le message et le CTA s'adaptent. Le prix
  // d'entrée est injecté (non-abonnés) pour lever la friction du « combien ? ».
  const priceLabel = entryPrice
    ? `dès ${entryPrice.price}${entryPrice.currency === 'USD' ? '$' : ` ${entryPrice.currency}`}`
    : null;
  const title = 'Limite quotidienne atteinte';
  const message = hasActiveSubscription
    ? `Votre plan ${currentPlan?.name ?? ''} inclut ${noun} par jour, et vous les avez toutes utilisées. Passez à un plan supérieur pour un accès illimité.`
    : isExpired
      ? `Vous avez utilisé vos ${noun} gratuites du jour. Renouvelez votre abonnement${priceLabel ? ` (${priceLabel}/mois)` : ''} pour retrouver un accès illimité.`
      : `Vous avez utilisé vos ${noun} gratuites du jour. Débloquez un accès illimité${priceLabel ? ` ${priceLabel}/mois` : ''} et continuez votre préparation maintenant.`;
  const cta = hasActiveSubscription
    ? 'Changer de plan'
    : isExpired
      ? 'Renouveler mon abonnement'
      : 'Voir les plans';

  return (
    <div className="flex flex-col items-center justify-center min-h-full text-center p-8">
      <div className="max-w-md mx-auto bg-overlay-1 border border-line rounded-2xl p-8">
        <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-lg">
          <Hourglass className="w-7 h-7 text-white" />
        </div>
        <h2 className="text-lg font-bold text-fg mb-2">{title}</h2>
        <p className="text-sm text-fg-subtle mb-6 leading-relaxed">{message}</p>
        <div className="flex flex-col gap-2.5">
          {canPurchaseInApp ? (
            <Link
              to="/plans"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl text-sm font-semibold active:scale-[0.98] transition-all"
            >
              <Sparkles className="w-4 h-4" />
              {cta}
            </Link>
          ) : (
            <WebPurchaseNotice className="text-xs text-fg-subtle leading-relaxed" />
          )}
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 text-fg-subtle hover:text-fg text-sm font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
        </div>
      </div>
    </div>
  );
}
